import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Pencil, Plus } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  listCoupons,
  getCoupon,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  ApiError,
  type CouponCampaignRow,
  type MailReward,
} from "@/lib/api";
import { formatTimestamp, cn } from "@/lib/utils";
import { ItemPicker } from "@/components/ItemPicker";
import { IapPicker } from "@/components/IapPicker";
import { HERO_NAMES, REWARD_TYPE_NAMES } from "@/types/cloud-data";

type EditorState = { mode: "new" } | { mode: "edit"; code: string };

export function CouponsRoute() {
  const qc = useQueryClient();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["coupons"],
    queryFn: listCoupons,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["coupons"] });

  const toggleActive = useMutation({
    mutationFn: async (row: CouponCampaignRow) => {
      // Recipients live outside the stored campaign JSON (campaign.emails is
      // always []), so a toggle must fetch the real recipient list first —
      // omitting `recipients` here would fall back to campaign.emails ([])
      // on the server and silently wipe targeting.
      const detail = await getCoupon(row.code);
      await updateCoupon(row.code, { campaign: detail.campaign, recipients: detail.recipients, active: !row.active });
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (code: string) => deleteCoupon(code),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
    },
  });

  const campaigns = query.data?.campaigns ?? [];

  return (
    <AppShell
      title="Coupon campaigns"
      subtitle="Private (D1-backed) coupon campaigns. Recipient targeting is never exposed to players."
    >
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground max-w-xl">
            Recipients are stored separately from the campaign JSON and are only ever
            sent to a player if their PlayFab id is on the list (or the list is empty,
            meaning everyone). The campaign JSON itself always carries{" "}
            <code className="font-mono">emails: []</code> — the player-facing endpoint
            never reveals who a campaign targets.
          </p>
          <Button size="sm" onClick={() => setEditor({ mode: "new" })} disabled={editor !== null}>
            <Plus className="h-3.5 w-3.5" /> New campaign
          </Button>
        </div>

        {editor && (
          <CouponEditor
            key={editor.mode === "edit" ? editor.code : "new"}
            state={editor}
            existingCodes={campaigns.map((c) => c.code)}
            onClose={() => setEditor(null)}
            onSaved={() => {
              setEditor(null);
              invalidate();
            }}
          />
        )}

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="px-4 py-2 border-b border-border/60 flex items-center justify-between">
            <h3 className="text-sm font-medium">Campaigns</h3>
            <span className="text-xs text-muted-foreground">
              {campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}
            </span>
          </div>
          {query.isLoading ? (
            <div className="p-4"><Skeleton className="h-24 w-full" /></div>
          ) : query.isError ? (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertDescription>Failed to load: {String(query.error)}</AlertDescription>
              </Alert>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">No coupon campaigns yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Active</th>
                  <th className="px-3 py-2 text-left">Recipients</th>
                  <th className="px-3 py-2 text-left">Updated</th>
                  <th className="px-3 py-2 text-left">Rewards</th>
                  <th className="px-3 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((row) => (
                  <tr key={row.code} className="border-t border-border/50 align-top">
                    <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => toggleActive.mutate(row)}
                        disabled={toggleActive.isPending}
                        title="Toggle active"
                      >
                        <Badge variant={row.active ? "success" : "outline"} className="cursor-pointer">
                          {row.active ? "Active" : "Inactive"}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {row.recipient_count === 0 ? "everyone" : row.recipient_count}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(row.updated_at)}
                    </td>
                    <td className="px-3 py-2 text-xs max-w-xs text-muted-foreground">
                      {formatCouponRewards(row.campaign.rewardDatas)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => setEditor({ mode: "edit", code: row.code })}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmDelete(row.code)}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {toggleActive.error && (
            <div className="p-3">
              <Alert variant="destructive">
                <AlertDescription>{String(toggleActive.error)}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        {confirmDelete && (
          <div className="rounded-lg border border-destructive/40 bg-card p-4 space-y-3">
            <p className="text-sm">
              Delete coupon campaign <span className="font-mono">{confirmDelete}</span> and its recipient list? This
              cannot be undone.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => remove.mutate(confirmDelete)}
                disabled={remove.isPending}
              >
                {remove.isPending ? "Deleting…" : "Confirm delete"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)} disabled={remove.isPending}>
                Cancel
              </Button>
            </div>
            {remove.error && (
              <Alert variant="destructive">
                <AlertDescription>{String(remove.error)}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

type CouponReward = MailReward;

interface RewardTypeDef {
  hasAmount?: boolean;
  needsItem?: boolean;
  needsHero?: boolean;
  needsBuff?: boolean;
  needsRecipe?: boolean;
  needsIap?: boolean;
}

// Keep this in lockstep with Send Mail: these fields mirror GameManager.GetReward.
const REWARD_TYPES: Record<number, RewardTypeDef> = {
  0: { hasAmount: true, needsItem: true },
  1: { hasAmount: true },
  2: { hasAmount: true },
  4: { needsHero: true },
  10: { hasAmount: true, needsBuff: true },
  11: { hasAmount: true },
  12: { hasAmount: true },
  14: { hasAmount: true },
  15: { hasAmount: true },
  16: { needsRecipe: true },
  100: { hasAmount: true, needsIap: true },
};

const DEFAULT_REWARD: CouponReward = { rewardType: 2, amount: 100 };

function formatCouponRewards(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return "No rewards";
  return value.map((raw) => {
    const reward = raw as Partial<CouponReward>;
    const name = typeof reward.rewardType === "number" ? REWARD_TYPE_NAMES[reward.rewardType] ?? `Type ${reward.rewardType}` : "Unknown";
    if (reward.rewardType === 0) return `${reward.itemID || "Item"} ×${reward.amount ?? 0}`;
    if (reward.rewardType === 4) return HERO_NAMES[reward.hero ?? 0] ?? "Hero";
    return `${name}${reward.amount !== undefined ? ` ×${reward.amount}` : ""}`;
  }).join(", ");
}

function parseRecipients(raw: string): string[] {
  return [...new Set(raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean))];
}

interface CouponEditorProps {
  state: EditorState;
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => void;
}

function CouponEditor({ state, existingCodes, onClose, onSaved }: CouponEditorProps) {
  const isEdit = state.mode === "edit";
  const [code, setCode] = useState(isEdit ? state.code : "");
  const [active, setActive] = useState(true);
  const [recipientsText, setRecipientsText] = useState("");
  const [rewards, setRewards] = useState<CouponReward[]>([]);
  const [loaded, setLoaded] = useState(!isEdit);

  const detail = useQuery({
    queryKey: ["coupon", isEdit ? state.code : null],
    queryFn: () => getCoupon((state as { mode: "edit"; code: string }).code),
    enabled: isEdit,
  });

  useEffect(() => {
    if (isEdit && !loaded && detail.isSuccess && detail.data) {
      setActive(!!detail.data.active);
      setRecipientsText(detail.data.recipients.join("\n"));
      setRewards(Array.isArray(detail.data.campaign.rewardDatas) ? detail.data.campaign.rewardDatas as CouponReward[] : []);
      setLoaded(true);
    }
  }, [isEdit, loaded, detail.isSuccess, detail.data]);

  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedCode = code.trim();
      if (!trimmedCode) throw new Error("Code is required.");
      if (!isEdit && existingCodes.includes(trimmedCode)) throw new Error("A campaign with this code already exists.");
      const campaign = { code: trimmedCode, emails: [] as string[], rewardDatas: rewards };
      const recipients = parseRecipients(recipientsText);
      if (isEdit) {
        await updateCoupon(trimmedCode, { campaign, recipients, active });
      } else {
        await createCoupon({ code: trimmedCode, campaign, recipients, active });
      }
    },
    onError: (e) => setFormError(e instanceof ApiError ? e.message : String((e as Error).message ?? e)),
    onSuccess: onSaved,
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    save.mutate();
  }

  const busy = save.isPending || (isEdit && detail.isLoading);

  function updateReward(index: number, patch: Partial<CouponReward>) {
    setRewards((current) => current.map((reward, i) => i === index ? { ...reward, ...patch } : reward));
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{isEdit ? `Edit ${state.code}` : "New campaign"}</h3>
        <button
          type="button"
          onClick={() => setActive((a) => !a)}
          className="text-xs"
          title="Toggle active"
        >
          <Badge variant={active ? "success" : "outline"} className="cursor-pointer">
            {active ? "Active" : "Inactive"}
          </Badge>
        </button>
      </div>

      {isEdit && detail.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : isEdit && detail.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load campaign: {String(detail.error)}</AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isEdit}
              placeholder="e.g. LAUNCH_WEEK_2026"
              className="font-mono max-w-sm"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Recipients — newline or comma separated PlayFab ids. Leave empty to target everyone.
              Stored separately from the campaign JSON; never sent to players.
            </label>
            <textarea
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              rows={4}
              spellCheck={false}
              placeholder="One PlayFab id per line (empty = everyone)"
              className={cn(
                "flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              )}
            />
          </div>

          <div className="space-y-3">
            <label className="text-xs text-muted-foreground">Rewards</label>
            {rewards.map((reward, index) => {
              const definition = REWARD_TYPES[reward.rewardType] ?? {};
              return (
                <div key={index} className="flex gap-2 items-center flex-wrap">
                  <select
                    value={reward.rewardType}
                    onChange={(e) => updateReward(index, {
                      rewardType: parseInt(e.target.value), amount: 1, itemID: undefined, hero: undefined,
                      developerBuffID: undefined, recipe: undefined, iapProductId: undefined,
                    })}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {Object.entries(REWARD_TYPE_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  {definition.hasAmount && <Input type="number" value={reward.amount} onChange={(e) => updateReward(index, { amount: parseInt(e.target.value) || 0 })} className="w-28" />}
                  {definition.needsIap && <IapPicker value={reward.iapProductId ?? ""} onChange={(iapProductId) => updateReward(index, { iapProductId })} />}
                  {definition.needsItem && <ItemPicker className="flex-1" value={reward.itemID ?? ""} onChange={(itemID) => updateReward(index, { itemID })} />}
                  {definition.needsHero && <select value={reward.hero ?? ""} onChange={(e) => updateReward(index, { hero: parseInt(e.target.value) })} className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"><option value="" disabled>Pick a hero</option>{Object.entries(HERO_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>}
                  {definition.needsBuff && <Input value={reward.developerBuffID ?? ""} onChange={(e) => updateReward(index, { developerBuffID: e.target.value })} placeholder="Buff ID" className="flex-1" />}
                  {definition.needsRecipe && <Input type="number" value={reward.recipe ?? ""} onChange={(e) => updateReward(index, { recipe: parseInt(e.target.value) || 0 })} placeholder="Recipe ID" className="w-28" />}
                  <Button type="button" variant="destructive" size="sm" onClick={() => setRewards((current) => current.filter((_, i) => i !== index))}>✕</Button>
                </div>
              );
            })}
            <Button type="button" variant="secondary" size="sm" onClick={() => setRewards((current) => [...current, { ...DEFAULT_REWARD }])}>+ Add Reward</Button>
          </div>

          {formError && (
            <Alert variant="destructive">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-border/60">
            <Button type="submit" size="sm" disabled={busy}>
              {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create campaign"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
